import api from "./apiWithAccessToken";

export const BILLING_MODES = {
  DEMO: "demo",
  PAYG: "payg",
};

export const TENANT_STATUSES = {
  ACTIVE: "active",
  SUSPENDED: "suspended",
  PENDING_DELETE: "pending_delete",
  DELETED: "deleted",
};

export const getBackendErrorMessage = (error, fallback = "Something went wrong.") =>
  error?.response?.data?.msg ||
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  fallback;

export const listPlatformTenants = ({ includeDeleted = false } = {}) =>
  api.get("/platform/tenants", {
    params: {
      include_deleted: includeDeleted,
    },
  });

export const createPlatformTenant = (payload) =>
  api.post("/platform/tenants", payload);

export const getPlatformTenant = (tenantId) =>
  api.get(`/platform/tenants/${tenantId}`);

export const updatePlatformTenant = (tenantId, payload) =>
  api.patch(`/platform/tenants/${tenantId}`, payload);

export const suspendPlatformTenant = (tenantId) =>
  api.post(`/platform/tenants/${tenantId}/suspend`);

export const activatePlatformTenant = (tenantId) =>
  api.post(`/platform/tenants/${tenantId}/activate`);

export const requestPlatformTenantDeletion = (tenantId) =>
  api.delete(`/platform/tenants/${tenantId}`);

export const runPlatformTenantDeletionJob = (tenantId, jobId) =>
  api.post(`/platform/tenants/${tenantId}/deletion-jobs/${jobId}/run`);

export const getPlatformTenantLlmRuntimeOptions = (tenantId) =>
  api.get(`/platform/tenants/${tenantId}/llm-runtime/options`);

export const getPlatformTenantLlmRuntimeActive = (tenantId) =>
  api.get(`/platform/tenants/${tenantId}/llm-runtime/active`);

export const getPlatformTenantLlmRuntimeDrafts = (tenantId) =>
  api.get(`/platform/tenants/${tenantId}/llm-runtime/drafts`);

export const getPlatformTenantLlmRuntimeProfiles = (tenantId) =>
  api.get(`/platform/tenants/${tenantId}/llm-runtime/profiles`);

export const createPlatformTenantLlmRuntimeDraft = (tenantId, payload) =>
  api.post(`/platform/tenants/${tenantId}/llm-runtime/drafts`, payload);

export const patchPlatformTenantLlmRuntimeDraft = (tenantId, profileId, payload) =>
  api.patch(`/platform/tenants/${tenantId}/llm-runtime/drafts/${profileId}`, payload);

export const activatePlatformTenantLlmRuntimeDraft = (tenantId, profileId) =>
  api.post(`/platform/tenants/${tenantId}/llm-runtime/drafts/${profileId}/activate`);

export const deletePlatformTenantLlmRuntimeDraft = (tenantId, profileId) =>
  api.delete(`/platform/tenants/${tenantId}/llm-runtime/drafts/${profileId}`);

export const deletePlatformTenantLlmRuntimeProfile = (tenantId, profileId) =>
  api.delete(`/platform/tenants/${tenantId}/llm-runtime/profiles/${profileId}`);

export const replacePlatformTenantLlmRuntimeCredential = (tenantId, payload) =>
  api.put(`/platform/tenants/${tenantId}/llm-runtime/credential`, payload);

export function getTenantEditInitialState(tenant) {
  return {
    displayName: tenant?.display_name || "",
    billingMode: tenant?.billing_mode || BILLING_MODES.PAYG,
    demoTokenLimit:
      tenant?.demo_token_limit === null || tenant?.demo_token_limit === undefined
        ? ""
        : String(tenant.demo_token_limit),
  };
}

export function validateTenantEditForm(formState) {
  const errors = {};
  const displayName = String(formState.displayName || "").trim();
  const billingMode = String(formState.billingMode || "").trim().toLowerCase();
  const rawLimit = String(formState.demoTokenLimit || "").trim();

  if (!displayName) {
    errors.displayName = "Display name is required.";
  }

  if (![BILLING_MODES.DEMO, BILLING_MODES.PAYG].includes(billingMode)) {
    errors.billingMode = "Account type must be demo or pay as you go.";
  }

  if (billingMode === BILLING_MODES.DEMO) {
    if (!rawLimit) {
      errors.demoTokenLimit = "Demo token limit is required.";
    } else if (!/^\d+$/.test(rawLimit)) {
      errors.demoTokenLimit = "Demo token limit must be a non-negative integer.";
    }
  }

  return errors;
}

export function buildTenantUpdatePayload(formState, originalTenant) {
  const errors = validateTenantEditForm(formState);
  if (Object.keys(errors).length > 0) {
    return { payload: {}, errors, changed: false };
  }

  const payload = {};
  const displayName = String(formState.displayName || "").trim();
  const billingMode = String(formState.billingMode || BILLING_MODES.PAYG)
    .trim()
    .toLowerCase();
  const originalDisplayName = String(originalTenant?.display_name || "").trim();
  const originalBillingMode = String(originalTenant?.billing_mode || BILLING_MODES.PAYG)
    .trim()
    .toLowerCase();

  if (displayName !== originalDisplayName) {
    payload.display_name = displayName;
  }

  if (billingMode !== originalBillingMode) {
    payload.billing_mode = billingMode;
  }

  if (billingMode === BILLING_MODES.DEMO) {
    const nextLimit = Number(String(formState.demoTokenLimit || "").trim());
    const originalLimit =
      originalTenant?.demo_token_limit === null || originalTenant?.demo_token_limit === undefined
        ? null
        : Number(originalTenant.demo_token_limit);

    if (nextLimit !== originalLimit) {
      payload.demo_token_limit = nextLimit;
    }
  }

  return {
    payload,
    errors: {},
    changed: Object.keys(payload).length > 0,
  };
}
